// Cấu hình bot Telegram lắng nghe nhóm -> tự đăng bài sang Kênh cộng đồng.
// Chỉ admin thấy. Token bot nằm ở .env trên server, KHÔNG nhập ở đây.
//
// Bố cục: dải tóm tắt (bật/tắt + số liệu) → 5 khối đánh số theo thứ tự việc phải
// làm. Phần "cách hoạt động" và "nguồn bot vừa nghe" gập lại vì chỉ đọc lúc khai
// lần đầu; mở sẵn khi còn nguồn chưa khai. Thân bảng cuộn riêng để nút Lưu luôn
// nhìn thấy — trước đây phải cuộn hết 5 khối mới tới nút.
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Alert, Collapse, message } from "antd";
import communityApi from "../../../api/communityApi";
import TelegramRecentSources, { countPendingSources } from "./telegram-recent-sources";
import TelegramGeneralSettings from "./telegram-general-settings";
import TelegramConfigSummaryBar from "./telegram-config-summary-bar";
import {
    TelegramChatList,
    TelegramSenderList,
    TelegramHashtagRouteList,
} from "./telegram-mapping-lists";

const emptyChat = { chatId: "", threadId: "", category: "" };
const emptySender = { id: "", label: "", category: null };
const emptyRoute = { hashtag: "", category: "" };

const HOW_IT_WORKS =
    "Bot nghe nhóm/kênh đã khai; tin của các tài khoản trong danh sách sẽ tự thành bài trên app (hiện ngay). " +
    "Sửa tin bên Telegram → bài sửa theo. Xoá bài: trả lời lệnh xoá vào tin đó. " +
    "Danh mục lấy theo thứ tự: thẻ trong bài → danh mục riêng của người đăng → danh mục của nhóm.";

// Mỗi khối một màu + vạch dọc bên trái: bảng dài, tiêu đề đen thui giống hệt nhau
// thì cuộn tới đâu cũng không biết đang ở phần nào.
// Viết đủ chuỗi class (không ghép động) để Tailwind quét ra mà giữ lại.
const SECTION_STYLE = {
    blue: { bar: "border-blue-400", text: "text-blue-700" },
    purple: { bar: "border-purple-400", text: "text-purple-700" },
    green: { bar: "border-green-500", text: "text-green-700" },
    orange: { bar: "border-orange-400", text: "text-orange-700" },
    cyan: { bar: "border-cyan-500", text: "text-cyan-700" },
};

/** Khối có số thứ tự + tiêu đề + mô tả 1 dòng. */
const Section = ({ step, title, hint, color = "blue", children }) => {
    const style = SECTION_STYLE[color] || SECTION_STYLE.blue;
    return (
        <div className={`mb-5 pl-3 border-l-4 ${style.bar}`}>
            <div className={`font-semibold ${style.text}`}>
                {step ? `${step}. ` : ""}
                {title}
            </div>
            {hint && <div className="text-xs text-gray-500 mb-2">{hint}</div>}
            {children}
        </div>
    );
};

export default function CommunityTelegramModal({ open, onClose, categories = [] }) {
    const [config, setConfig] = useState(null);
    const [hasToken, setHasToken] = useState(false);
    // Nhóm/người worker vừa thấy nhưng chưa khai — bấm 1 nút là thêm, khỏi chép id
    const [recentSources, setRecentSources] = useState([]);
    const [ownerName, setOwnerName] = useState(null);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchConfig = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await communityApi.getTelegramConfig();
            setConfig(data.config);
            setHasToken(data.hasToken);
            setRecentSources(data.recentSources || []);
            setOwnerName(data.ownerName || null);
            setOffset(data.offset || 0);
        } catch {
            message.error("Không tải được cấu hình Telegram");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchConfig();
    }, [open, fetchConfig]);

    const patch = (changes) => setConfig((prev) => ({ ...prev, ...changes }));
    const patchList = (key, index, changes) =>
        patch({ [key]: config[key].map((item, i) => (i === index ? { ...item, ...changes } : item)) });
    const removeAt = (key, index) => patch({ [key]: config[key].filter((_, i) => i !== index) });

    /** Thêm nhanh cả nhóm lẫn người từ 1 dòng "nguồn gần đây". */
    const addFromSource = (src) => {
        const hasChat = config.chats.some(
            (c) => c.chatId === src.chatId && (c.threadId || "") === (src.threadId || "")
        );
        const hasSender = config.senders.some((s2) => s2.id === src.senderId);
        patch({
            chats: hasChat
                ? config.chats
                : [...config.chats, { chatId: src.chatId, threadId: src.threadId || "", category: "" }],
            senders: hasSender
                ? config.senders
                : [...config.senders, { id: src.senderId, label: src.senderName || "", category: null }],
        });
    };

    // Gợi ý cho ô nhập: gộp trùng theo id
    const chatOptions = [...new Map(recentSources.map((s2) => [s2.chatId, s2])).values()].map((s2) => ({
        value: s2.chatId,
        label: `${s2.chatTitle || "(nhóm không tên)"} — ${s2.chatId}`,
    }));
    const senderOptions = [...new Map(recentSources.map((s2) => [s2.senderId, s2])).values()].map((s2) => ({
        value: s2.senderId,
        label: `${s2.senderName || "?"} — ${s2.senderId}`,
    }));
    const categoryOptions = categories.map((c) => ({ value: c.name, label: c.name }));
    const pendingCount = config ? countPendingSources(recentSources, config) : 0;

    const handleSave = async () => {
        try {
            setSaving(true);
            const { data } = await communityApi.updateTelegramConfig(config);
            setConfig(data.config);
            message.success("Đã lưu cấu hình");
            fetchConfig();
        } catch (err) {
            message.error(err?.response?.data?.message || "Không lưu được cấu hình");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title="Bot Telegram → tự đăng bài"
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText="Lưu"
            cancelText="Đóng"
            confirmLoading={saving}
            width={900}
            // Thân cuộn riêng: nút Lưu đứng yên ở chân bảng, khỏi cuộn ngược lên tìm
            styles={{ body: { maxHeight: "68vh", overflowY: "auto", paddingRight: 8 } }}
        >
            {!hasToken && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Chưa có TELEGRAM_INGEST_BOT_TOKEN trên server"
                    description="Cần đặt TELEGRAM_INGEST_BOT_TOKEN trong .env của backend rồi restart telegram-worker, không thì bot không chạy."
                />
            )}

            {config && (
                <>
                    <TelegramConfigSummaryBar
                        config={config}
                        offset={offset}
                        pendingCount={pendingCount}
                    />

                    <Collapse
                        ghost
                        size="small"
                        className="mb-3"
                        items={[{ key: "how", label: "Cách hoạt động", children: <div className="text-sm">{HOW_IT_WORKS}</div> }]}
                    />

                    <Section
                        step={1}
                        color="blue"
                        title="Nguồn bot vừa nghe được"
                        hint="Nhóm/kênh thì bấm “Thêm làm nguồn”; chat riêng của bạn thì bấm “Dùng để báo riêng”."
                    >
                        {/* Đã khai xong hết thì gập lại — danh sách này chỉ dùng lúc khai lần đầu. */}
                        <Collapse
                            size="small"
                            defaultActiveKey={pendingCount > 0 ? ["src"] : []}
                            items={[
                                {
                                    key: "src",
                                    label:
                                        pendingCount > 0
                                            ? `${pendingCount} nguồn chưa khai`
                                            : `Đã khai hết (${recentSources.length} nguồn bot từng nghe)`,
                                    children: (
                                        <TelegramRecentSources
                                            sources={recentSources}
                                            config={config}
                                            onAddSource={addFromSource}
                                            onUseAsNotify={(src) =>
                                                patch({ notifyChatId: String(src.chatId) })
                                            }
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>

                    <Section step={2} color="purple" title="Cài đặt chung">
                        <TelegramGeneralSettings
                            config={config}
                            patch={patch}
                            loading={loading}
                            ownerName={ownerName}
                            senderOptions={senderOptions}
                        />
                    </Section>

                    <Section
                        step={3}
                        color="green"
                        title="Nhóm Telegram → danh mục"
                        hint="Bài từ nhóm này sẽ vào đúng danh mục đã chọn"
                    >
                        <TelegramChatList
                            chats={config.chats}
                            chatOptions={chatOptions}
                            categoryOptions={categoryOptions}
                            onChange={(i, changes) => patchList("chats", i, changes)}
                            onRemove={(i) => removeAt("chats", i)}
                            onAdd={() => patch({ chats: [...config.chats, { ...emptyChat }] })}
                        />
                    </Section>

                    <Section
                        step={4}
                        color="orange"
                        title="Tài khoản Telegram được phép đăng"
                        hint="Tin của người ngoài danh sách bị bỏ qua. Bỏ trống danh mục = theo danh mục của nhóm."
                    >
                        <TelegramSenderList
                            senders={config.senders}
                            senderOptions={senderOptions}
                            categoryOptions={categoryOptions}
                            onChange={(i, changes) => patchList("senders", i, changes)}
                            onRemove={(i) => removeAt("senders", i)}
                            onAdd={() => patch({ senders: [...config.senders, { ...emptySender }] })}
                        />
                    </Section>

                    <Section
                        step={5}
                        color="cyan"
                        title="Hashtag → danh mục"
                        hint="Thẻ trong bài đè danh mục của nhóm và của người đăng, nên cùng 1 kênh vẫn rải được nhiều tab. Thẻ tự gỡ khỏi bài. Không gắn thẻ nào = giữ danh mục như cũ."
                    >
                        <TelegramHashtagRouteList
                            routes={config.hashtagRoutes || []}
                            categoryOptions={categoryOptions}
                            onChange={(i, changes) => patchList("hashtagRoutes", i, changes)}
                            onRemove={(i) => removeAt("hashtagRoutes", i)}
                            onAdd={() =>
                                patch({ hashtagRoutes: [...(config.hashtagRoutes || []), { ...emptyRoute }] })
                            }
                        />
                    </Section>
                </>
            )}
        </Modal>
    );
}
